import { signIn, useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthPayload from '@/types/authPayloadType';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';
import { getAuthInfo } from '@/apis/ApiUtil';
import useUserAuthIntegrationsStore from '@/state/userAuthIntegrationsState';
import axiosInstance from '@/lib/httpAdapter';
import apiUrl from '@/apis/api';
import { AxiosError } from 'axios';

/**
 * Returns true for errors that should be silently suppressed in fire-and-forget
 * contexts (useEffect callbacks). These are:
 * - AxiosError with 401 status (already handled by httpAdapter interceptor)
 * - AxiosError with ERR_CANCELED code (expected during navigation/unmount)
 */
export function isSuppressedAuthError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    const httpStatus = error.response?.status;
    if (httpStatus === 401 || error.code === 'ERR_CANCELED') {
      return true;
    }
  }
  return false;
}

/**
 * Returns true for transient network errors that are worth retrying.
 * These are errors where no HTTP response was received at all (network-level
 * failures), not HTTP error codes like 401/403/500.
 *
 * NOTE: ECONNABORTED (request timeout) is intentionally excluded. The default
 * axios timeout is 30s — retrying would make the user wait 60-90s+ for a
 * request that is likely to time out again. Timeouts indicate real backend
 * slowness and should be logged, not retried.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    // ERR_NETWORK = no response received (CORS, DNS, offline, backend down)
    return error.code === 'ERR_NETWORK';
  }
  // For non-Axios errors (e.g., native fetch), fall back to message matching
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('network error') ||
      msg.includes('failed to fetch') ||
      msg.includes('load failed') ||
      msg.includes('networkerror')
    );
  }
  return false;
}

const MAX_SAVE_AUTH_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;

export const ValidateSession = () => {
  const { data: session, status } = useSession();
  const { integrations, getIntegrationByName, setInitialIntegrations } =
    useUserAuthIntegrationsStore();
  const organization = useOrganizationStore((state) => state.organization);
  const user = useUserStore((state) => state.user);
  const [userUpdated, setUserUpdated] = useState(false);
  const saveAuthRetryCount = useRef(0);
  const retryAbortControllerRef = useRef<AbortController | null>(null);

  const needsUpdate = async () => {
    if (new Date(session?.expires as string) < new Date()) {
      try {
        await signIn(session?.user?.provider);
      } catch (error) {
        // signIn can fail due to network errors during re-authentication.
        // Log but don't re-throw since this is called from useEffect.
        console.error('Failed to re-authenticate expired session:', error);
      }
    }
  };

  const updateAuthInfoCallback = useCallback(async () => {
    // Cancel any in-flight retry from a previous call
    retryAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    retryAbortControllerRef.current = abortController;

    saveAuthRetryCount.current = 0;

    while (!abortController.signal.aborted) {
      // Re-read user and org from Zustand stores on each attempt to avoid
      // stale values after a session refresh. Session fields (accessToken,
      // provider, refreshToken, username) use the closure value since
      // useSession() can only be read via the React hook.
      const user = useUserStore.getState().user;
      const org = useOrganizationStore.getState().organization;
      const payloadSaveAuthInfo: AuthPayload = {
        AccessToken: session?.user?.accessToken,
        IntegrationName: session?.user?.provider,
        UserUUID: user?.UserUUID,
        RefreshToken: session?.user?.refreshToken,
      };

      if (userUpdated) return;

      if (org?.OrgUUID || user?.OrganizationUUID) {
        payloadSaveAuthInfo.OrgUUID = user?.OrganizationUUID ?? org?.OrgUUID;
      }

      if (session?.user?.provider === 'github') {
        payloadSaveAuthInfo.GithubUserName = session?.user?.username;
      }

      // Google is identity-only (no SCM username). Only non-github, non-google
      // providers carry a GitLab-style username here; sending GitlabUserName for
      // a Google login would mislabel the account as GitLab on the backend.
      if (
        session?.user?.provider !== 'github' &&
        session?.user?.provider !== 'google'
      ) {
        payloadSaveAuthInfo.GitlabUserName = session?.user?.username;
      }

      if (
        !(payloadSaveAuthInfo?.OrgUUID || payloadSaveAuthInfo?.UserUUID) ||
        !session?.user?.accessToken
      ) {
        return;
      }

      try {
        const responseSaveAuthInfo = await axiosInstance.post(
          `${apiUrl}saveAuthInfo/`,
          payloadSaveAuthInfo
        );

        if (
          responseSaveAuthInfo.status === 200 &&
          !responseSaveAuthInfo.data.Error
        ) {
          setUserUpdated(true);
          saveAuthRetryCount.current = 0;
        }

        return responseSaveAuthInfo.data;
      } catch (error) {
        // Suppress 401 and ERR_CANCELED silently -- these are already handled
        // by the httpAdapter interceptor or are expected during navigation.
        if (isSuppressedAuthError(error)) {
          return undefined;
        }

        // Retry transient network errors (ERR_NETWORK) with exponential backoff
        // before giving up.
        if (
          isTransientNetworkError(error) &&
          saveAuthRetryCount.current < MAX_SAVE_AUTH_RETRIES
        ) {
          saveAuthRetryCount.current += 1;
          const delay =
            RETRY_BASE_DELAY_MS * Math.pow(2, saveAuthRetryCount.current - 1);
          await new Promise((resolve) => setTimeout(resolve, delay));
          // Loop continues — payload will be rebuilt from current state
          continue;
        }

        // Exhausted retries or non-transient error -- log and swallow so the
        // exception doesn't propagate as an unhandled rejection.
        saveAuthRetryCount.current = 0;
        console.error('Failed to save auth info:', error);
        return undefined;
      }
    }
  }, [
    session?.user?.accessToken,
    session?.user?.provider,
    session?.user?.refreshToken,
    session?.user?.username,
    userUpdated,
  ]);

  const getUserAuthInfo = async () => {
    try {
      let userResponse;

      if (integrations.length === 0) {
        const userUUID = user?.UserUUID ?? '';
        const provider = session?.user?.provider ?? '';
        const orgUUID = user?.OrganizationUUID ?? organization?.OrgUUID ?? '';

        if (!userUUID || !provider) {
          return;
        }

        userResponse = await getAuthInfo(userUUID, provider, orgUUID);
      } else {
        userResponse = { Integrations: integrations };
      }

      if (
        Array.isArray(userResponse?.Integrations) &&
        userResponse.Integrations.length > 0
      ) {
        setInitialIntegrations(userResponse.Integrations);

        const userUUID = user?.UserUUID;
        const provider = session?.user?.provider;

        if (userUUID && provider) {
          const integration = getIntegrationByName(provider, userUUID);
          // GitHub tokens are redacted from fetchAuthInfo, so a value comparison
          // would differ on every load and re-save on each render. Save only when
          // the row itself is missing; GitHub OAuth tokens do not rotate on their
          // own, they change only on a fresh authorization.
          const needsSave =
            provider === 'github'
              ? !integration
              : integration?.AccessToken !== session?.user?.accessToken;
          if (needsSave) {
            await updateAuthInfoCallback();
          }
        }
      }
    } catch (error) {
      // This is a fire-and-forget callback invoked from useEffect, so we must
      // never re-throw. Suppress 401 and ERR_CANCELED; log all other errors.
      if (!isSuppressedAuthError(error)) {
        console.error('Failed to get user auth info:', error);
      }
    }
  };

  useEffect(() => {
    if (
      status !== 'loading' &&
      session?.user &&
      session?.user?.accessToken &&
      session?.user?.provider
    ) {
      needsUpdate();
    }
    // needsUpdate is a plain function recreated every render; adding it would
    // rerun this effect on every render. Depend on the primitive fields it
    // actually reacts to (accessToken, provider, expires) instead of the
    // whole `session` object -- next-auth hands back a new session object
    // identity on revalidation even when these fields are unchanged, which
    // was refiring this effect on every such revalidation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    session?.user?.accessToken,
    session?.user?.provider,
    session?.expires,
  ]);

  // Trigger an explicit re-signIn only when the JWT callback flagged the refresh
  // as failed. The previous heuristic (no accessToken AND no expires_at) fired
  // whenever the JWT callback silently corrupted those fields, which it did on
  // every GitLab page load once expires_at lapsed, because the provider lookup
  // resolved to undefined and the refresh URL was missing. That produced the
  // Firefox redirect loop on /home. With token.error flowing through to the
  // session, this fires once per genuine refresh failure.
  const sessionError = (session as typeof session & { error?: string })?.error;

  useEffect(() => {
    // 'RefreshAccessTokenError' — GitLab/generic OAuth refresh failed.
    // 'NoRefreshToken' — refreshBitbucketToken in [...nextauth]/route.ts sets this
    // when a Bitbucket session lacks a refresh_token at all (e.g. the original
    // signIn predates refresh-token support, or the integration was revoked).
    // Without this branch those users sit in an authenticated-but-expired state
    // forever — accessToken expired, no path to recover except a manual sign-out.
    if (
      status === 'authenticated' &&
      sessionError &&
      ['RefreshAccessTokenError', 'NoRefreshToken'].includes(sessionError)
    ) {
      signIn(session?.user?.provider, {
        redirect: true,
        callbackUrl: window.location.href,
      }).catch((error: unknown) => {
        // signIn can fail due to network errors during re-authentication.
        // Log but don't re-throw since this is a fire-and-forget call from useEffect.
        console.error('Failed to re-authenticate session:', error);
      });
    }
    // Depend on the primitive sessionError/provider values instead of the
    // whole `session` object -- its identity changes on every next-auth
    // revalidation even when these fields haven't, which was refiring this
    // effect on every such revalidation.
  }, [status, sessionError, session?.user?.provider]);

  useEffect(() => {
    if (
      (user?.UserUUID && session?.user?.accessToken) ||
      (status === 'authenticated' && !user?.UserUUID)
    ) {
      if (session?.user?.provider) {
        getUserAuthInfo();
      }
    }
    return () => {
      setUserUpdated(false);
      // Cancel any pending retry so it doesn't fire with stale state
      retryAbortControllerRef.current?.abort();
    };
    // getUserAuthInfo is a plain function recreated every render; adding it
    // would rerun this effect on every render. Depend on the primitive
    // identities this effect actually reacts to (UserUUID, accessToken,
    // provider, integrations count, status) instead of the user/session/
    // integrations objects -- those get a new identity on every render or
    // store update, which was refiring this effect and re-hitting the
    // backend (getUserAuthInfo -> getAuthInfo, uncached) on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.UserUUID,
    session?.user?.accessToken,
    session?.user?.provider,
    integrations.length,
    status,
  ]);

  return <></>;
};
